import { PagingParameters } from "@/api/dtos";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import {
  artworkColumnsFormatted,
  artworkColumnsVisibility,
  getArtworkForComplexLink,
} from "@/config/dashboard/artwork";
import { generateTable } from "@/config/dashboard/general";
import { FieldConfig } from "@/config/forms/general";
import { Artwork, ArtworkInBibliography, Bibliography } from "@/lib/types";
import { ColumnDef, Row } from "@tanstack/react-table";
import { ChevronLeft, PlusIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import ArtworkInBibliographyDrawer from "./drawer/ArtworkInBibliographyDrawer";

interface ComplexLinkProps {
  id: string;
  artwork: Artwork | undefined;
  bibliography: Bibliography | undefined;
  fields: FieldConfig[] | undefined;
  type: LinkableType;
}

const columnsForBibliography = [
  {
    accessorKey: "bibliography.bibliographieDescriptionNumeroBibliographie",
    header: "bibliographieDescriptionNumeroBibliographie",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<ArtworkInBibliography> }) => {
      return (
        <ImageCell
          value={""}
          attachments={row.original.attachments ?? []}
          domain={"bibliography"}
        />
      );
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionTitre",
    header: "bibliographieDescriptionTitre",
    size: 150,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionAnnee",
    header: "bibliographieDescriptionAnnee",
    size: 50,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionAuteur",
    header: "bibliographieDescriptionAuteur",
    size: 150,
    cell: (props: any) => {
      return <TextCell value={props.getValue()} />;
    },
  },
];

/**
 * ArtworkInBibliographyLinks component
 * @returns {JSX.Element}
 */
const ArtworkInBibliographyLinks = ({
  id,
  artwork,
  fields,
  type,
  bibliography,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "artworkInBibliography",
    type,
  );

  const { mutate: deleteArtworkInBibliography } = useDeleteComplexLink(
    id,
    "artworkInBibliography",
    type,
  );

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteArtworkInBibliography({
        id,
        relation: "artworkInBibliography",
      });
    },
    [deleteArtworkInBibliography],
  );

  const [searchContent, setSearchContent] = useState<string>("");
  const columns: ColumnDef<ArtworkInBibliography, any>[] = useMemo(() => {
    const columnArtwork = getArtworkForComplexLink() as ColumnDef<
      ArtworkInBibliography,
      any
    >[];
    return [
      ...(type === "artwork"
        ? columnsForBibliography.map((column) => ({
            ...column,
            header: t(column.header),
          }))
        : columnArtwork.map((column) => ({
            ...column,
            header: typeof column.header === "string" ? t(column.header) : "",
          }))),
      {
        accessorKey: "oeuvresDansBibliographieDescriptionReproduitPage",
        header: t("oeuvresDansBibliographieDescriptionReproduitPage"),
        size: 50,
      },
      {
        accessorKey: "oeuvresDansBibliographieDescriptionCitePage",
        header: t("oeuvresDansBibliographieDescriptionCitePage"),
        size: 50,
      },
      {
        accessorKey: "oeuvresDansBibliographieDescriptionReproductionCouleur",
        header: t("oeuvresDansBibliographieDescriptionReproductionCouleur"),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? t("yes") : t("no")} />;
        },
      },
      {
        accessorKey: "oeuvresDansBibliographieDescriptionNonPagine",
        header: t("oeuvresDansBibliographieDescriptionNonPagine"),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? t("yes") : t("no")} />;
        },
      },
      {
        accessorKey: "oeuvresDansBibliographieNotes",
        header: t("oeuvresDansBibliographieNotes"),
        cell: (props: any) => {
          return <HtmlCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: (props: any) => {
          const data = props.row.original as ArtworkInBibliography;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(data.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <ArtworkInBibliographyDrawer
                key={props.row.original.id}
                fields={fields}
                data={data}
                queryId={id}
                type={type}
              />
              {type === "bibliography" && (
                <ShowElements
                  type="artwork"
                  id={props.row.original.artwork.id}
                  elements={[
                    {
                      label: t("oeuvresDescriptionTitre"),
                      value: props.row.original.artwork.oeuvresDescriptionTitre,
                      type: "text",
                    },
                    {
                      label: t("oeuvresDescriptionNumeroEnregistrement"),
                      value:
                        props.row.original.artwork
                          .oeuvresDescriptionNumeroEnregistrement,
                      type: "text",
                    },
                    {
                      label: t("oeuvresDescriptionResume"),
                      value:
                        props.row.original.artwork.oeuvresDescriptionResume,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: props.row.original.artwork.attachments,
                      type: "image",
                    },
                  ]}
                />
              )}
              {type === "artwork" && (
                <ShowElements
                  type="bibliography"
                  id={props.row.original.bibliography.id}
                  elements={[
                    {
                      label: t("bibliographieDescriptionNumeroBibliographie"),
                      value:
                        props.row.original.bibliography
                          .bibliographieDescriptionNumeroBibliographie,
                      type: "text",
                    },
                    {
                      label: t("bibliographieDescriptionResumeLong"),
                      value:
                        props.row.original.bibliography
                          .bibliographieDescriptionResumeLong,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: props.row.original.attachments,
                      type: "image",
                    },
                  ]}
                />
              )}
            </div>
          );
        },
      },
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const datatableConf = useMemo(() => {
    if (type === "artwork") {
      return (
        <ArtworkToBibliographyLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          artwork={artwork}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    } else {
      return (
        <BibliographyToArtworkLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          bibliography={bibliography}
          isAddMode={isAddMode}
          columnsLink={columns}
          searchContent={searchContent}
        />
      );
    }
  }, [
    artwork,
    bibliography,
    columns,
    fields,
    id,
    isAddMode,
    relatedData,
    searchContent,
    type,
  ]);

  return (
    <div className="w-full">
      {isAddMode && (
        <div
          className="flex items-center gap-1 mb-4 cursor-pointer"
          onClick={() => setIsAddMode(false)}
        >
          <ChevronLeft className="w-3 h-3 text-gray-300" />
          <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
            {t("relatedRecords")}
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">
          {type !== "artwork"
            ? t("artworksRelated")
            : t("bibliographiesRelated")}
        </h2>
        {!isAddMode && (
          <Button variant="outline" onClick={() => setIsAddMode(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("add")}
          </Button>
        )}
        {isAddMode && (
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        )}
      </div>
      {datatableConf}
    </div>
  );
};

type ArtworkToBibliographyLinksProps =
  LinksTableProps<ArtworkInBibliography> & {
    artwork: Artwork | undefined;
  };

type BibliographyToArtworkLinksProps =
  LinksTableProps<ArtworkInBibliography> & {
    bibliography: Bibliography | undefined;
  };

//Composant pour Oeuvres
const ArtworkToBibliographyLinks = ({
  id,
  relatedData,
  fields,
  artwork,
  isAddMode,
  columnsLink,
  searchContent,
}: ArtworkToBibliographyLinksProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notRelatedBibliographies } =
    useGetNotRelatedResource<Bibliography>({
      resourceId: id,
      resultType: "bibliography",
      searchQuery: searchContent,
      pagination: getPagination,
    });

  const columnsBibliographies: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographieDescriptionNumeroBibliographie"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2 text-nowrap">
              <p className="text-[14px] font-[500]">
                {row.original.bibliographieDescriptionNumeroBibliographie}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"bibliography"}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionTitre",
        header: t("bibliographieDescriptionTitre"),
        size: 100,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "bibliographieDescriptionAnnee",
        header: t("bibliographieDescriptionAnnee"),
        size: 100,
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.bibliographieDescriptionAnnee} />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionAuteur",
        header: t("bibliographieDescriptionAuteur"),
        size: 200,
        cell: (props) => {
          return <TextCell value={props.getValue()} />;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }) => {
          const data = row.original as Bibliography;
          if (!artwork) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="bibliography"
                id={row.original.id}
                elements={[
                  {
                    label: t("bibliographieDescriptionNumeroBibliographie"),
                    value:
                      row.original.bibliographieDescriptionNumeroBibliographie,
                    type: "text",
                  },
                  {
                    label: t("bibliographieDescriptionResumeLong"),
                    value: row.original.bibliographieDescriptionResumeLong,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
              <ArtworkInBibliographyDrawer
                key={row.original.id}
                fields={fields}
                queryId={artwork.id}
                type="artwork"
                data={{
                  id: "",
                  bibliography: data,
                  artwork: artwork,
                  oeuvresDansBibliographieDescriptionReproduitPage: "",
                  oeuvresDansBibliographieDescriptionCitePage: "",
                  oeuvresDansBibliographieDescriptionPhotographe: "",
                  oeuvresDansBibliographieDescriptionReproductionCouleur: false,
                  oeuvresDansBibliographieDescriptionNonPagine: false,
                  oeuvresDansBibliographieNotes: "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [artwork, fields, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsBibliographies}
          data={notRelatedBibliographies?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notRelatedBibliographies?.totalSize ?? 0,
          }}
          options={{
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsBibliographies,
      columnsLink,
      getPagination,
      isAddMode,
      notRelatedBibliographies?.data,
      notRelatedBibliographies?.totalSize,
      relatedData,
    ],
  );

  return datatableConf;
};

// Composant pour Bibliographies
const BibliographyToArtworkLinks = ({
  id,
  relatedData,
  fields,
  bibliography,
  isAddMode,
  columnsLink,
  searchContent,
}: BibliographyToArtworkLinksProps) => {
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  const { data: notAlreadyRelatedArtworks } = useGetNotRelatedResource<Artwork>(
    {
      resourceId: id,
      resultType: "artwork",
      searchQuery: searchContent,
      pagination: getPagination,
    },
  );

  const { t } = useTranslation();

  const columnsArtworks: ColumnDef<Artwork, any>[] = useMemo(() => {
    return [
      ...generateTable(artworkColumnsFormatted, "artwork"),
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Artwork> }) => {
          const data = row.original as Artwork;
          if (!bibliography) return null;
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <ShowElements
                type="artwork"
                id={row.original.id}
                elements={[
                  {
                    label: t("oeuvresDescriptionTitre"),
                    value: row.original.oeuvresDescriptionTitre,
                    type: "text",
                  },
                  {
                    label: t("oeuvresDescriptionNumeroEnregistrement"),
                    value: row.original.oeuvresDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("oeuvresDescriptionResume"),
                    value: row.original.oeuvresDescriptionResume,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
              <ArtworkInBibliographyDrawer
                key={row.original.id}
                fields={fields}
                queryId={bibliography.id}
                type="bibliography"
                data={{
                  id: "",
                  bibliography: bibliography,
                  artwork: data,
                  oeuvresDansBibliographieDescriptionReproduitPage: "",
                  oeuvresDansBibliographieDescriptionCitePage: "",
                  oeuvresDansBibliographieDescriptionPhotographe: "",
                  oeuvresDansBibliographieDescriptionReproductionCouleur: false,
                  oeuvresDansBibliographieDescriptionNonPagine: false,
                  oeuvresDansBibliographieNotes: "",
                }}
              />
            </div>
          );
        },
      },
    ];
  }, [bibliography, fields, t]);

  const datatableConf = useMemo(
    () =>
      isAddMode ? (
        <DataTable
          columns={columnsArtworks}
          data={notAlreadyRelatedArtworks?.data ?? []}
          pagination={{
            pagination: getPagination,
            setPage: setPagination,
            totalSize: notAlreadyRelatedArtworks?.totalSize ?? 0,
          }}
          options={{
            state: {
              columnVisibility: {
                ...artworkColumnsVisibility,
                oeuvresDescriptionVignette: true,
                oeuvresPublicationStatus: false,
              },
            },
            enableColumnResizing: true,
          }}
        />
      ) : (
        <DataTable
          columns={columnsLink}
          data={relatedData ?? []}
          options={{
            enableColumnResizing: true,
          }}
        />
      ),
    [
      columnsArtworks,
      columnsLink,
      getPagination,
      isAddMode,
      notAlreadyRelatedArtworks?.data,
      notAlreadyRelatedArtworks?.totalSize,
      relatedData,
    ],
  );

  return datatableConf;
};

export default ArtworkInBibliographyLinks;
